import { create } from 'zustand'
import type { StateCreator } from 'zustand'

import type { Script } from '@/features/script/domain/models/script.ts'
import { calculateScriptDuration } from '@/utils/calculate-script-duration.ts'
import { useScriptStore } from '@/shared/store/script.store.ts'
import type { Voice } from '@/features/voice/domain/models/voice.ts'
import { SpeechContentType, type TabOption } from './types.ts'
import type { FolderContent } from '@/features/media-file/domain/models/media-folder.ts'
import type {
  MediaFile,
  AudioFileSource,
  RecordingSource,
} from '@/features/media-file/domain/models/media-file.ts'
import { fetchVoices } from '@/features/voice/domain/use-cases/fetch-voices.ts'
import type { Clip } from '@/features/clips/domain/models/clip'

import { useSpeechStore } from '@/shared/store/speech.store.ts'
import { countEmojis } from '@/features/script/domain/use-cases/count-emojis.ts'
import { countMultibyte } from '@/features/script/domain/use-cases/count-multibyte.ts'
import { isRecordingSource } from '@/features/media-file/domain/predicates/is-recording-source.ts'
import { uploadFileToPath } from '@/features/media-file/domain/use-cases/upload-file-to-path.ts'
import { isMediaSource } from '@/features/media-file/domain/predicates/is-media-source.ts'
import { fetchFileInfo } from '@/features/media-file/domain/use-cases/fetch-file-info.ts'

const { clearPreview: clearSpeechPreview } = useSpeechStore.getState()

const MAX_CHARS = 23 * 600 // 23 chars per second, 600 seconds
const COUNT_IN_SECONDS = 3

const getVoiceTechnology = (selectedTab: TabOption): 'TTS' | 'STS' =>
  selectedTab === 'speech' ? 'STS' : 'TTS'

const getBaseVoiceFilters = (
  tech: 'TTS' | 'STS',
  expressive: boolean
): {
  technologies: Array<'TTS' | 'STS'>
  providers?: string[]
} => ({
  technologies: [tech],
  ...(expressive ? { providers: ['elevenlabs'] } : {}),
})

const getCompatibleSelectedVoice = (
  selectedVoice: Voice | undefined,
  tech: 'TTS' | 'STS',
  expressive: boolean
): Voice | undefined => {
  if (!selectedVoice) return undefined

  const isCompatible =
    tech === 'TTS' ? selectedVoice.hasTTS : selectedVoice.hasSTS
  if (!isCompatible || (expressive && !selectedVoice.isExpressive)) {
    return undefined
  }

  return selectedVoice
}

const getFallbackVoice = async (
  expressive: boolean,
  filters: ReturnType<typeof getBaseVoiceFilters>,
  voices: Voice[]
): Promise<Voice | undefined> => {
  if (expressive) {
    return voices[0]
  }

  const { items: wrenVoices } = await fetchVoices(0, filters, 'Wren')
  return wrenVoices[0] ?? voices[0]
}

interface Store {
  script?: Script | undefined
  scriptDuration: number
  speed: number
  expressive: boolean
  selectedVoice?: Voice | undefined
  voices: Voice[]
  scriptText: string
  scriptTextError: string
  audioFilesError?: string | undefined
  contentType: SpeechContentType
  mediaFilesCurrentFolder: FolderContent | undefined
  selectedTab: TabOption
  selectedLibraryFiles: MediaFile[]
  audioFiles: AudioFileSource[]
  selectedAudioFileId: string | undefined
  editingClip?: Clip | undefined // Track the clip being edited
  setSelectedTab: (type: TabOption) => void
  setSelectedLibraryFiles: (files: MediaFile[]) => void
  setAudioFiles: (files: AudioFileSource[]) => void
  setEditingClip: (clip?: Clip) => void
  addRecordingAudioFile: (file: File) => void
  addUploadAudioFile: (file: File) => void
  addLibraryAudioFiles: (mediaFiles: MediaFile[]) => void
  setSelectedAudioFileId: (fileId: string | undefined) => void
  getSelectedAudioSource: () => AudioFileSource | undefined
  setMediaFilesCurrentFolder: (content: FolderContent) => void
  uploadLocalFile: (fileId: string) => Promise<void>
  validateScriptText: (value: string) => boolean
  validateAudioFiles: () => boolean
  setScript: (script: Script) => void
  setScriptText: (value: string) => void
  setScriptFormError: (error: string) => void
  setSpeed: (speed: number) => void
  setExpressive: (expressive: boolean) => void
  setContentType: (type: SpeechContentType) => void
  selectScript: (scriptId?: string) => void
  fetchVoices: () => Promise<void>
  reset: () => void
  recordOpen: boolean
  setRecordOpen: (open: boolean) => void
  isRecording: boolean
  setIsRecording: (recording: boolean) => void
  recordedAudio: string | null
  setRecordedAudio: (audioUrl: string | null) => void
  stopRecorderFn: () => void
  setStopRecorderFn: (fn: () => void) => void
  countIn: number
  setCountIn: (countIn: number) => void
  countdown: number | undefined
  setCountdown: (value?: number) => void
  selectVoice: (voice: Voice) => void
  stereo: boolean
  setStereo: (v: boolean) => void
  autoGain: boolean
  setAutoGain: (v: boolean) => void
  noiseSuppression: boolean
  setNoiseSuppression: (v: boolean) => void
  echoCancel: boolean
  setEchoCancel: (v: boolean) => void
}

const stateCreator: StateCreator<Store> = (set, get) => ({
  speed: 1,
  expressive: false,
  scriptText: '',
  scriptTextError: '',
  scriptDuration: 0,
  voices: [],
  contentType: SpeechContentType.Main,
  mediaFilesCurrentFolder: undefined,
  selectedTab: 'text',
  selectedLibraryFiles: [] as MediaFile[],
  audioFiles: [] as AudioFileSource[],
  selectedAudioFileId: undefined,
  editingClip: undefined,
  setScript: (script: Script) => {
    set({ script })
  },
  setSelectedTab: (type: TabOption) => {
    const { selectedVoice } = get()
    set({ selectedTab: type })

    if (selectedVoice) {
      const isCompatible =
        type === 'text' ? selectedVoice.hasTTS : selectedVoice.hasSTS
      if (!isCompatible) {
        set({ selectedVoice: undefined })
      }
    }

    clearSpeechPreview() // IF tab changes. clear speech preview
  },
  setSelectedLibraryFiles: (files: MediaFile[]) => {
    set({ selectedLibraryFiles: files })
  },
  setAudioFiles: (files: AudioFileSource[]) => {
    set({ audioFiles: files })
    if (files.length > 0) {
      set({ audioFilesError: undefined })
    }
  },
  setEditingClip: (clip?: Clip) => {
    set({ editingClip: clip })
  },
  getSelectedAudioSource: () => {
    const { audioFiles, selectedAudioFileId } = get()
    return audioFiles.find(({ id }) => id === selectedAudioFileId)
  },
  addRecordingAudioFile: (file: File) => {
    const { audioFiles } = get()
    const newAudioFile: RecordingSource = {
      id: crypto.randomUUID(),
      kind: 'recording',
      file,
    }
    const updatedAudioFiles = [newAudioFile, ...audioFiles]
    set({
      audioFiles: updatedAudioFiles,
      audioFilesError:
        updatedAudioFiles.length === 0
          ? 'ls_speech_dialog_errors_missing_file'
          : undefined,
      selectedAudioFileId: updatedAudioFiles[0]?.id,
    })
  },
  addUploadAudioFile: (file: File) => {
    const { audioFiles } = get()
    const newAudioFile: AudioFileSource = {
      id: crypto.randomUUID(),
      kind: 'upload',
      file,
    }
    const updatedAudioFiles = [newAudioFile, ...audioFiles]
    set({
      audioFiles: updatedAudioFiles,
      audioFilesError:
        updatedAudioFiles.length === 0
          ? 'ls_speech_dialog_errors_missing_file'
          : undefined,
      selectedAudioFileId: updatedAudioFiles[0]?.id,
    })
  },
  addLibraryAudioFiles: (mediaFiles: MediaFile[]) => {
    const { audioFiles } = get()
    const newAudioFiles = mediaFiles.map(
      (mediaFile) =>
        ({
          id: crypto.randomUUID(),
          kind: 'media',
          mediaFile,
        }) as const
    )
    const updatedAudioFiles = [...newAudioFiles, ...audioFiles]
    set({
      audioFiles: updatedAudioFiles,
      audioFilesError:
        updatedAudioFiles.length === 0
          ? 'ls_speech_dialog_errors_missing_file'
          : undefined,
      selectedAudioFileId: updatedAudioFiles[0]?.id,
    })
  },
  setSelectedAudioFileId: (fileId: string | undefined) => {
    set({ selectedAudioFileId: fileId })
    clearSpeechPreview()
  },
  setContentType: (type: SpeechContentType) => {
    set({ contentType: type })
  },
  setMediaFilesCurrentFolder: (content: FolderContent) => {
    set({ mediaFilesCurrentFolder: content })
  },
  uploadLocalFile: async (fileId: string) => {
    const { audioFiles } = get()
    const audioFile = audioFiles.find((file) => file.id === fileId)
    if (!audioFile) {
      console.error('Audio file not found')
      return
    }
    if (isMediaSource(audioFile)) {
      console.error('Audio file is already uploaded')
      return
    }
    const uploadPath = isRecordingSource(audioFile)
      ? 'home/recordings'
      : 'home/uploads'
    const uploadId = await uploadFileToPath(audioFile.file, uploadPath, 'voice')
    const mediaFile = await fetchFileInfo(uploadId)
    set(({ audioFiles }) => ({
      audioFiles: audioFiles.map((file) =>
        file.id === fileId ? { ...file, mediaFile } : file
      ),
    }))
  },
  fetchVoices: async () => {
    const { expressive, selectedVoice, selectedTab } = get()
    const tech = getVoiceTechnology(selectedTab)
    const baseFilters = getBaseVoiceFilters(tech, expressive)
    const { items: voices } = await fetchVoices(0, baseFilters, '')
    let voiceToSet = getCompatibleSelectedVoice(selectedVoice, tech, expressive)

    voiceToSet ??= await getFallbackVoice(expressive, baseFilters, voices)

    set({ voices, selectedVoice: voiceToSet })
  },
  selectVoice: (voice: Voice) => {
    set((state) => ({
      selectedVoice: voice,
      expressive: voice.isExpressive ? state.expressive : false,
    }))
    clearSpeechPreview()
  },
  setExpressive: (expressive: boolean) => {
    set((state) => ({
      expressive,
      selectedVoice:
        expressive && state.selectedVoice && !state.selectedVoice.isExpressive
          ? state.voices.find((voice) => voice.isExpressive)
          : state.selectedVoice,
    }))
    clearSpeechPreview()
  },
  setSpeed: (speed: number) => {
    set({ speed })
    clearSpeechPreview()
  },
  validateScriptText: (value: string) => {
    const missingContent = !value
    const tooManyEmojis = countEmojis(value) >= 2
    const tooLong = countMultibyte(value) >= MAX_CHARS
    if (missingContent) {
      set({ scriptTextError: 'ls_new_script_text_required' })
      return false
    }
    if (tooManyEmojis) {
      set({ scriptTextError: 'ls_sidebar_scripts_errors_too_many_emojis' })
      return false
    }
    if (tooLong) {
      set({ scriptTextError: 'ls_sidebar_scripts_errors_too_long' })
      return false
    }
    set({ scriptTextError: '' })
    return true
  },
  validateAudioFiles: () => {
    const { audioFiles } = get()
    if (audioFiles.length < 1) {
      set({ audioFilesError: 'ls_speech_dialog_errors_missing_file' })
      return false
    }
    const [firstFile] = audioFiles
    if (!firstFile?.mediaFile) {
      set({ audioFilesError: 'ls_speech_dialog_errors_not_uploaded_yet' })
      return false
    }

    set({
      audioFilesError: undefined,
      selectedAudioFileId: firstFile.id,
    })
    return true
  },
  setScriptText: (value: string) => {
    set({ scriptText: value, scriptDuration: calculateScriptDuration(value) })
    // Clear any existing errors when user starts typing
    if (get().scriptTextError) {
      set({ scriptTextError: '' })
    }
    clearSpeechPreview() // IF text script changes. clear speech preview
  },
  selectScript: (scriptId?: string) => {
    if (!scriptId) {
      set({
        script: undefined,
        scriptText: '',
        scriptTextError: '',
        scriptDuration: 0,
      })
      return
    }
    const { scripts } = useScriptStore.getState()
    const script = scripts.find((s) => s.id === scriptId)
    if (!script) return
    set({
      script,
      scriptText: script.text,
      scriptDuration: calculateScriptDuration(script.text),
    })
  },
  setScriptFormError: (error: string) => {
    set((prev) => ({ ...prev, scriptTextError: error }))
  },
  reset: () => {
    set({
      contentType: SpeechContentType.Main,
      expressive: false,
      isRecording: false,
      audioFiles: [],
      selectedAudioFileId: undefined,
      editingClip: undefined,
      audioFilesError: undefined,
    })
  },
  recordOpen: false,
  setRecordOpen: (open: boolean) => {
    set({ recordOpen: open })
  },
  isRecording: false,
  setIsRecording: (recording: boolean) => {
    set({ isRecording: recording })
  },
  recordedAudio: null,
  setRecordedAudio: (audioUrl: string | null) => {
    set({ recordedAudio: audioUrl })
  },
  stopRecorderFn: () => {
    /* no-op by default */
  },
  setStopRecorderFn: (fn: () => void) => {
    set({ stopRecorderFn: fn })
  },
  countIn: COUNT_IN_SECONDS,
  setCountIn: (countIn: number) => {
    set({ countIn })
  },
  countdown: undefined,
  setCountdown: (value?: number) => {
    set({ countdown: value })
  },
  stereo: false,
  setStereo: (value: boolean) => {
    set({ stereo: value })
  },
  autoGain: false,
  setAutoGain: (value: boolean) => {
    set({ autoGain: value })
  },
  noiseSuppression: false,
  setNoiseSuppression: (value: boolean) => {
    set({ noiseSuppression: value })
  },
  echoCancel: false,
  setEchoCancel: (value: boolean) => {
    set({ echoCancel: value })
  },
})
const useSpeechDialogStore = create<Store>(stateCreator)

export default useSpeechDialogStore
